/**
 * /fleet — the shared vehicle pool.
 *
 * Trucks aren't owned by a person; they're booked per task. This screen is the
 * fleet registry: each vehicle is a card linking to its detail page (profile,
 * photos, service log, issue reports). Status — active / in the shop /
 * retired — and an open-issue badge are visible at a glance.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Loader2, Plus, Truck, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Vehicle, VehicleStatus, VehicleType } from '@vari-crew/schemas';
import { useCreateVehicle, useVehicles } from '@/api/vehicles';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

const VEHICLE_TYPES: { value: VehicleType; label: string }[] = [
  { value: 'cargo_van', label: 'Cargo van' },
  { value: 'box_truck', label: 'Box truck' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'trailer', label: 'Trailer' },
  { value: 'other', label: 'Other' },
];
export const typeLabel = (t: VehicleType) =>
  VEHICLE_TYPES.find((x) => x.value === t)?.label ?? t;
const selectCls = 'h-8 rounded border border-line bg-bg px-2 text-sm';

export const STATUS_META: Record<VehicleStatus, { label: string; cls: string }> = {
  active: { label: 'Active', cls: 'border-status-complete text-status-complete-fg bg-status-complete/30' },
  out_of_service: {
    label: 'In the shop',
    cls: 'border-status-enroute text-status-enroute-fg bg-status-enroute/30',
  },
  retired: { label: 'Retired', cls: 'border-line text-ink-faint' },
};

export function StatusBadge({ status }: { status: VehicleStatus }) {
  const m = STATUS_META[status];
  return (
    <span className={cn('text-[11px] px-1.5 py-0.5 rounded-full border font-medium', m.cls)}>
      {m.label}
    </span>
  );
}

export function Fleet() {
  const [showRetired, setShowRetired] = useState(false);
  const { data, isLoading, error } = useVehicles(showRetired);
  const [adding, setAdding] = useState(false);

  const vehicles = data?.data ?? [];
  const activeCount = vehicles.filter((v) => v.status === 'active').length;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Truck className="w-5 h-5 text-ink-dim" />
            Fleet
          </h1>
          <p className="text-sm text-ink-dim">
            {activeCount} active {activeCount === 1 ? 'vehicle' : 'vehicles'} · a shared pool,
            booked per task.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-ink-dim">
            <input
              type="checkbox"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
            />
            Show retired
          </label>
          <Button size="sm" onClick={() => setAdding((v) => !v)}>
            <Plus className="w-4 h-4" />
            Add vehicle
          </Button>
        </div>
      </header>

      {adding && <AddVehiclePanel onClose={() => setAdding(false)} />}

      {isLoading ? (
        <div className="py-10 grid place-items-center">
          <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
        </div>
      ) : error ? (
        <div className="py-8 text-sm text-status-blocked-fg">{(error as Error).message}</div>
      ) : vehicles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-ink-dim">
            No vehicles yet. Add your first truck to start booking it on jobs.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {vehicles.map((v) => (
            <VehicleCard key={v.id} vehicle={v} />
          ))}
        </div>
      )}
    </div>
  );
}

function VehicleCard({ vehicle }: { vehicle: Vehicle }) {
  const subtitle = [
    typeLabel(vehicle.type),
    [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || null,
    vehicle.plate,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Link
      to={`/fleet/${vehicle.id}`}
      className={cn(
        'group rounded-xl border border-line bg-bg p-4 hover:bg-fill-soft transition-colors',
        vehicle.status === 'retired' && 'opacity-60',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-fill-soft grid place-items-center shrink-0">
            <Truck className="w-5 h-5 text-ink-dim" />
          </div>
          <div className="min-w-0">
            <div className="font-mono font-semibold">{vehicle.label}</div>
            <div className="text-xs text-ink-dim truncate">{subtitle || '—'}</div>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-ink-faint group-hover:text-ink shrink-0 mt-1" />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <StatusBadge status={vehicle.status} />
        {vehicle.open_issue_count > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full border border-status-blocked text-status-blocked-fg bg-status-blocked/20 font-medium">
            <AlertTriangle className="w-3 h-3" />
            {vehicle.open_issue_count} open {vehicle.open_issue_count === 1 ? 'issue' : 'issues'}
          </span>
        )}
        {vehicle.status === 'out_of_service' && vehicle.status_note && (
          <span className="text-[11px] text-ink-faint truncate">— {vehicle.status_note}</span>
        )}
      </div>
    </Link>
  );
}

function AddVehiclePanel({ onClose }: { onClose: () => void }) {
  const create = useCreateVehicle();
  const [label, setLabel] = useState('');
  const [type, setType] = useState<VehicleType>('cargo_van');
  const [plate, setPlate] = useState('');

  async function submit() {
    if (!label.trim()) {
      toast.error('Give the vehicle a label (e.g. VARI-3).');
      return;
    }
    try {
      const created = await create.mutateAsync({
        label: label.trim(),
        type,
        plate: plate.trim() || null,
      });
      toast.success(`Added ${created.label}`);
      onClose();
    } catch (err) {
      toast.error(`Failed to add: ${(err as Error).message}`);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Add vehicle</CardTitle>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <label className="text-sm">
          <span className="text-xs text-ink-faint">Label</span>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="VARI-3"
            className="mt-1 h-8"
          />
        </label>
        <label className="text-sm">
          <span className="text-xs text-ink-faint">Type</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as VehicleType)}
            className={cn('mt-1 w-full', selectCls)}
          >
            {VEHICLE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="text-xs text-ink-faint">Plate (optional)</span>
          <Input
            value={plate}
            onChange={(e) => setPlate(e.target.value)}
            placeholder="TX-0000"
            className="mt-1 h-8"
          />
        </label>
        <div className="sm:col-span-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={create.isPending}>
            {create.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Add vehicle
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export { VEHICLE_TYPES, selectCls };
